import React, { Fragment, useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Alert from "react-bootstrap/Alert";
import DateTimePicker from "react-datetime-picker";
import { ButtonModalFormSaveClose } from "./ButtonModalSelection";
import AxisSpinner from "./AxisSpinner";
import { BsClock, BsFileEarmarkPdf, BsCalendarCheck, BsCalculator, BsExclamationTriangle } from "react-icons/bs";

const SubmitTimesheet = ({ shift, onComplete, close = null }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [startTimeValid, setStartTimeValid] = useState(false);
  const [endTimeValid, setEndTimeValid] = useState(false);
  const [hoursWorkedVal, setHoursWorkedVal] = useState(0);
  const [minsWorkedVal, setMinsWorkedVal] = useState(0);
  const [timesheetHoursWorked, setTimesheetHoursWorked] = useState(0);
  const [needFile, setNeedFile] = useState(true);
  const [file, setFile] = useState(null);
  const [fileValid, setFileValid] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [hoursValid, setHoursValid] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (shift === null || shift === undefined) return;

    if (shift?.timesheet?.start)
      setStartTime(new Date(shift?.timesheet?.start));
    else if (shift?.start) setStartTime(new Date(shift?.start));

    if (shift?.timesheet?.end) setEndTime(new Date(shift?.timesheet?.end));
    else if (shift?.end) setEndTime(new Date(shift?.end));

    const haveFile =
      shift?.timesheet !== null && shift?.timesheet !== undefined;
    setNeedFile(!haveFile);

    if (shift?.timesheet?.hoursWorked) {
      const hours = shift?.timesheet?.hoursWorked;
      const totalMins = hours * 60;
      const hrs = Math.floor(totalMins / 60);
      const mins = Math.round(totalMins % 60);
      setHoursWorkedVal(hrs);
      setMinsWorkedVal(mins);
    }
  }, [shift]);

  useEffect(() => {
    const valid = hoursWorkedVal > 0;
    setHoursValid(valid);
  }, [hoursWorkedVal]);

  useEffect(() => {
    const startValid = startTime !== null;
    setStartTimeValid(startValid);

    if (!startValid) {
      setEndTimeValid(endTime !== null);
    } else {
      if (endTime === null) {
        setEndTimeValid(false);
      } else {
        setEndTimeValid(endTime > startTime);
      }
    }
  }, [startTime, endTime]);

  useEffect(() => {
    if ((!needFile && file === null) || file === undefined) {
      setFileValid(true);
      return;
    }
    var valid =
      file?.type === "application/pdf" ||
      file?.type === "image/png" ||
      file?.type === "image/jpeg";
    setFileValid(valid);
  }, [file, needFile]);

  useEffect(() => {}, [saveDisabled]);

  useEffect(() => {
    const valid = startTimeValid && endTimeValid && fileValid && hoursValid;
    setSaveDisabled(!valid);
  }, [startTimeValid, endTimeValid, fileValid, hoursValid]);

  useEffect(() => {
    if (hoursWorkedVal < 0) {
      setHoursWorkedVal(0);
    }
    if (hoursWorkedVal > 16) {
      setHoursWorkedVal(16);
    }
    if (minsWorkedVal < 0) {
      setMinsWorkedVal(0);
    }
    if (minsWorkedVal > 59) {
      setMinsWorkedVal(59);
    }

    setTimesheetHoursWorked(
      (Number(hoursWorkedVal) * 60.0 + Number(minsWorkedVal)) / 60.0,
    );
  }, [hoursWorkedVal, minsWorkedVal]);

  const onSave = async (e) => {
    e.preventDefault();
    
    // Reset error state
    setError(null);
    setShowError(false);
    
    try {
      setLoading(true);

      const formData = new FormData();
      
      // Prepare form data
      if (needFile || file) formData.append("file", file);
      formData.append("user_id", shift?.user?.id);
      formData.append("shift_id", shift?.id);
      formData.append("start", startTime?.toISOString());
      formData.append("end", endTime?.toISOString());
      formData.append("hours_worked", timesheetHoursWorked);

      // Submit timesheet
      try {
        const response = await authAxios().post(
          "/api/backoffice/timesheet/submit",
          formData,
          {},
        );
  
        // Fetch updated shift data
        const fetchConfig = {
          headers: {
            "content-type": "application/json",
          },
          params: {
            shift_id: shift?.id,
          },
        };
  
        const shiftResponse = await authAxios().get(
          "/api/backoffice/shifts/" +
            (shift?.table === "shifts" ? "active" : "archive"),
          fetchConfig,
        );
  
        // Complete submission process
        setLoading(false);
        onComplete(shiftResponse.data);
      } catch (apiError) {
        // Handle API errors
        setLoading(false);
        let errorMessage = "Failed to submit timesheet. Please try again.";
        
        // Extract more specific error message if available
        if (apiError.response) {
          if (apiError.response.status === 400) {
            errorMessage = "Invalid timesheet data. Please check all fields and try again.";
          } else if (apiError.response.status === 401) {
            errorMessage = "Your session has expired. Please log in again.";
          } else if (apiError.response.status === 403) {
            errorMessage = "You don't have permission to submit this timesheet.";
          } else if (apiError.response.status === 404) {
            errorMessage = "The shift or user was not found.";
          } else if (apiError.response.status === 500) {
            errorMessage = "Server error while processing your timesheet. Please try again later.";
          }
          
          // If we have a more specific error message from the server
          if (apiError.response.data && apiError.response.data.message) {
            errorMessage = apiError.response.data.message;
          }
        }
        
        setError(errorMessage);
        setShowError(true);
        console.error("API Error:", apiError);
      }
    } catch (err) {
      // Handle unexpected errors
      setLoading(false);
      setError("An unexpected error occurred. Please try again.");
      setShowError(true);
      console.error("Unexpected error:", err);
    }
  };

  const hoursWorkedChanged = (value) => {
    var text = value.target.value;
    setHoursWorkedVal(parseInt(text.trim()));
  };

  const minsWorkedChanged = (value) => {
    var text = value.target.value;
    setMinsWorkedVal(parseInt(text.trim()));
  };

  const handleFileChanged = (value) => {
    if (value.target.files) {
      setFile(value.target.files[0]);
    }
  };
  
  // Support for drag and drop functionality
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg'];
      
      if (validTypes.includes(droppedFile.type)) {
        setFile(droppedFile);
      }
    }
  };

  const getConversionDetail = (type) => {
    return type === "application/pdf" ? type : type + " -> application/pdf";
  };

  const fileDetails = () => {
    if (file === null || file === undefined) return;
    return (
      <section>
        <ul>
          <li>Name: {file?.name}</li>
          <li>Type: {getConversionDetail(file?.type)}</li>
          <li>Size: {file?.size} bytes</li>
        </ul>
      </section>
    );
  };

  const fileInputRef = useRef(null);

  return (
    <Fragment style={{ position: "relative" }}>
      {loading && 
        <div className="overlay-spinner">
          <div className="text-center">
            <AxisSpinner loading={loading} />
            <div className="mt-2 fw-bold text-axis-offblack">Processing Timesheet...</div>
          </div>
        </div>
      }
      <Form onSubmit={onSave}>
        {/* Error Alert */}
        {showError && error && (
          <Alert 
            variant="danger" 
            className="mb-4" 
            dismissible 
            onClose={() => setShowError(false)}
          >
            <div className="d-flex align-items-center">
              <BsExclamationTriangle className="me-2" size={20} />
              <div>{error}</div>
            </div>
          </Alert>
        )}
        
        {/* Header with shift info */}
        <div className="mb-4 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-axis-offblack">
            Submit Timesheet 
            <Badge bg="primary" className="ms-2">
              {shift?.id ? `Shift #${shift?.id}` : 'New Timesheet'}
            </Badge>
          </h5>
          <div className="text-muted small">
            {shift?.facility?.name && <span>Facility: {shift?.facility?.name} | </span>}
            {shift?.ward?.identifier && <span>Ward: {shift?.ward?.identifier}</span>}
          </div>
        </div>

        {/* Time Information Section */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-light d-flex align-items-center">
            <BsClock className="me-2 text-primary" />
            <span className="fw-bold">Time Information</span>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formStart">
                  <Form.Label className="fw-semibold">Start Time <span className="text-danger">*</span></Form.Label>
                  <div className="time-picker-container">
                    <Form.Control
                      as={DateTimePicker}
                      onChange={setStartTime}
                      value={startTime}
                      isValid={startTimeValid}
                      isInvalid={!startTimeValid}
                      required
                      className="w-100 time-picker-custom"
                      format="y-MM-dd HH:mm"
                      disableClock={true}
                    />
                  </div>
                  <Form.Text className="text-muted">
                    Select when the shift started
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    Please select a valid start time
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formEnd">
                  <Form.Label className="fw-semibold">End Time <span className="text-danger">*</span></Form.Label>
                  <div className="time-picker-container">
                    <Form.Control
                      as={DateTimePicker}
                      onChange={setEndTime}
                      value={endTime}
                      isValid={endTimeValid}
                      isInvalid={!endTimeValid}
                      required
                      className="w-100 time-picker-custom"
                      format="y-MM-dd HH:mm"
                      disableClock={true}
                    />
                  </div>
                  <Form.Text className="text-muted">
                    Select when the shift ended
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    End time must be after start time
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Hours Worked Section */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-light d-flex align-items-center">
            <BsCalculator className="me-2 text-primary" />
            <span className="fw-bold">Hours Worked</span>
          </Card.Header>
          <Card.Body>
            <Form.Group controlId="formHoursWorked">
              <Form.Label className="fw-semibold">Total Time Worked <span className="text-danger">*</span></Form.Label>
              <Row className="align-items-center">
                <Col xs={12} md={6} lg={4}>
                  <div className="d-flex align-items-center">
                    <div className="me-2">
                      <Form.Control
                        type="number"
                        value={hoursWorkedVal}
                        onChange={hoursWorkedChanged}
                        isValid={hoursValid}
                        isInvalid={!hoursValid}
                        min="0"
                        max="16"
                        required
                        className="text-center"
                      />
                      <Form.Text className="text-center d-block">Hours</Form.Text>
                    </div>
                    <div className="time-separator fw-bold mx-2">:</div>
                    <div>
                      <Form.Control
                        type="number"
                        value={minsWorkedVal}
                        onChange={minsWorkedChanged}
                        isValid={hoursValid}
                        isInvalid={!hoursValid}
                        min="0"
                        max="59"
                        required
                        className="text-center"
                      />
                      <Form.Text className="text-center d-block">Minutes</Form.Text>
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={8}>
                  <div className="d-flex align-items-center h-100 mt-3 mt-md-0">
                    <div className="ms-md-3 p-2 bg-light rounded">
                      <span className="fw-semibold">Total: </span>
                      <span className="fw-bold text-primary">
                        {timesheetHoursWorked.toFixed(2)} hours
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
              <Form.Control.Feedback type="invalid">
                Please enter a valid number of hours (greater than 0)
              </Form.Control.Feedback>
            </Form.Group>
          </Card.Body>
        </Card>

        {/* Timesheet File Upload Section */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-light d-flex align-items-center">
            <BsFileEarmarkPdf className="me-2 text-primary" />
            <span className="fw-bold">Timesheet Document</span>
          </Card.Header>
          <Card.Body>
            <Form.Group controlId="formFile">
              <Form.Label className="fw-semibold">Upload Timesheet <span className="text-danger">*</span></Form.Label>
              <div className="file-upload-container p-3 border rounded mb-3 text-center" 
                   onClick={() => fileInputRef.current.click()}
                   onDragOver={handleDragOver}
                   onDrop={handleDrop}
                   style={{cursor: 'pointer', backgroundColor: '#f8f9fa', borderStyle: 'dashed'}}>
                <BsFileEarmarkPdf size={30} className="mb-2 text-primary" />
                <p className="mb-1">Click to browse or drag and drop</p>
                <p className="small text-muted mb-0">PDF, JPG or PNG files only</p>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChanged}
                  isValid={fileValid}
                  isInvalid={!fileValid}
                  required={needFile}
                  className="d-none"
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </div>
              
              {file && (
                <div className="file-details p-3 border rounded bg-light">
                  <div className="d-flex align-items-center">
                    <BsFileEarmarkPdf className="me-2 text-primary" />
                    <div className="flex-grow-1">
                      <div className="fw-semibold text-truncate">{file.name}</div>
                      <div className="small text-muted">
                        {getConversionDetail(file.type)} • {(file.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <Form.Control.Feedback type="invalid">
                Please upload a valid timesheet file (PDF, JPG or PNG)
              </Form.Control.Feedback>
            </Form.Group>
          </Card.Body>
        </Card>

        {/* Submit Button */}
        <div className="text-end mt-4">
          <ButtonModalFormSaveClose
            saveName={"Submit Timesheet"}
            close={close}
            formSave={onSave}
            loading={saveDisabled || loading}
          />
        </div>
      </Form>
    </Fragment>
  );
};

export default SubmitTimesheet;
